/**
 * Arrêt maladie d'un dirigeant de société : ce que touche le président de
 * SASU, et ce que touche le gérant d'EURL.
 *
 * DEUX RÉGIMES QUI NE SE RESSEMBLENT PAS. Le président de SASU est assimilé
 * salarié : il touche la moitié de son salaire journalier de base, plafonné à
 * 1,4 Smic, soit 42,97 € par jour au plus depuis juillet 2026, et seulement
 * s'il a cotisé sur au moins 1 015 fois le Smic horaire sur les six mois
 * précédents. Le gérant d'EURL est travailleur indépendant : il touche le
 * 730e de son revenu annuel moyen des trois dernières années, jusqu'à 65,84 €
 * par jour, 197,51 € s'il exerce une profession libérale réglementée.
 *
 * LE PIÈGE QUE PERSONNE NE FORMULE. Le salaire qui valide les quatre
 * trimestres de retraite, 7 212 € bruts par an, n'ouvre AUCUN droit aux
 * indemnités : il en faut 12 200,30 € sur six mois pour un arrêt qui commence
 * cette année, soit 2 033 € bruts par mois environ. Le président payé au
 * minimum, ou tout en dividendes, ne touche rien.
 *
 * CE QUE LE CALCUL SUPPOSE, faute de source qui tranche (relevé du 24
 * septembre 2026, voir la fiche) :
 *   - un salaire de président régulier, versé chaque mois ; payé par
 *     trimestre, la caisse pourrait ne retenir aucune paie ;
 *   - les 150 heures ne sont pas testées : on ignore comment la caisse compte
 *     les heures d'un mandataire sans contrat, et la condition de salaire est
 *     la prudente ;
 *   - le revenu du gérant est sa rémunération nette plus ses dividendes
 *     au-delà de 10 % du capital. Depuis 2025, la base des cotisations est
 *     le revenu cotisations comprises abattu de 26 % : on ne sait pas encore
 *     comment la caisse la reprendra dans le revenu annuel moyen, mais les deux
 *     bases donnent des montants proches pour un gérant.
 */

export const IJ_SOCIETE = {
    // Assimilé salarié (code de la sécurité sociale, R. 313-3 et R. 323-4).
    salarie: {
        taux: 0.5,
        diviseur: 91.25,
        plafondSmic: 1.4,
        ouvertureSmicHeures: 1015,
        ouvertureMois: 6,
    },
    // Travailleur indépendant (D. 622-7 et D. 622-8).
    independant: {
        diviseur: 730,
        revenuMinimum: 4582,
        plafond: 48060,
        plafondLiberalReglemente: 3 * 48060,
        joursMaxLiberalReglemente: 87,
        affiliationMois: 12,
    },
    carence: 3,
    // CSG 6,2 % et CRDS 0,5 %, sur 100 % de l'indemnité brute.
    prelevements: 0.067,
};

/**
 * Le Smic en vigueur, par date d'effet. Au-delà de la dernière date connue,
 * la dernière valeur reste en vigueur jusqu'à revalorisation : la revue du
 * 1er janvier oblige à relire cette table.
 */
export const SMIC_2026 = [
    { depuis: "2026-01-01", horaire: 12.02, mensuel: 1823.03 },
    { depuis: "2026-06-01", horaire: 12.31, mensuel: 1867.02 },
];

export function smicAu(isoDate) {
    let retenu = null;
    for (const s of SMIC_2026) if (s.depuis <= isoDate) retenu = s;
    return retenu;
}

const iso = (d) => d.toISOString().slice(0, 10);

/** Le premier jour du mois qui précède de `mois` mois celui de la date. */
function debutDeMois(date, mois) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - mois, 1, 12));
}

/**
 * Président de SASU, salaire brut mensuel régulier.
 *
 * @param salaireMensuel  salaire brut versé chaque mois
 * @param debut           date de début de l'arrêt (Date)
 * @param jours           durée de l'arrêt, en jours calendaires, six mois au plus
 */
export function arretSasu({ salaireMensuel, debut, jours }) {
    const s = IJ_SOCIETE.salarie;
    // Condition d'ouverture : les six mois civils qui précèdent l'arrêt, au
    // Smic horaire en vigueur le premier jour de cette période.
    const debutPeriode = debutDeMois(debut, s.ouvertureMois);
    const smicPeriode = smicAu(iso(debutPeriode));
    const seuilOuverture = smicPeriode ? Math.round(s.ouvertureSmicHeures * smicPeriode.horaire * 100) / 100 : null;
    const cotise = Math.max(0, salaireMensuel) * s.ouvertureMois;
    const ouvre = seuilOuverture !== null && cotise >= seuilOuverture;

    // Plafond : 1,4 Smic mensuel en vigueur le dernier jour du mois civil qui
    // précède l'arrêt.
    const veille = new Date(Date.UTC(debut.getUTCFullYear(), debut.getUTCMonth(), 0, 12));
    const smicVeille = smicAu(iso(veille));
    const plafondMensuel = smicVeille ? s.plafondSmic * smicVeille.mensuel : null;
    const retenu = plafondMensuel === null ? 0 : Math.min(Math.max(0, salaireMensuel), plafondMensuel);
    // La caisse paie un montant par jour, au centime : c'est lui qui se multiplie.
    const brut = ouvre ? Math.round(((s.taux * 3 * retenu) / s.diviseur) * 100) / 100 : 0;
    const maximum = plafondMensuel === null ? null : (s.taux * 3 * plafondMensuel) / s.diviseur;

    const joursIndemnises = Math.max(0, jours - IJ_SOCIETE.carence);
    return {
        forme: "sasu",
        ouvre,
        seuilOuverture,
        salaireMensuelOuverture: seuilOuverture === null ? null : seuilOuverture / s.ouvertureMois,
        plafondMensuel,
        plafonne: plafondMensuel !== null && salaireMensuel >= plafondMensuel,
        parJour: brut,
        maximum,
        joursIndemnises,
        total: brut * joursIndemnises,
        net: brut * joursIndemnises * (1 - IJ_SOCIETE.prelevements),
    };
}

/**
 * Gérant d'EURL, travailleur indépendant.
 *
 * @param revenuAnnuel   revenu annuel moyen des trois dernières années :
 *                       rémunération nette, plus dividendes au-delà de 10 %
 * @param reglemente     profession libérale réglementée (Cipav et autres
 *                       sections de la CNAVPL) : plafond de trois PASS, et
 *                       87 jours indemnisés au plus par arrêt
 * @param affilie        affilié comme indépendant depuis au moins un an
 * @param jours          durée de l'arrêt, en jours calendaires
 */
export function arretEurl({ revenuAnnuel, reglemente = false, affilie = true, jours }) {
    const t = IJ_SOCIETE.independant;
    const revenu = Math.max(0, revenuAnnuel);
    const plafond = reglemente ? t.plafondLiberalReglemente : t.plafond;
    const sousLeSeuil = revenu < t.revenuMinimum;
    const ouvre = affilie && !sousLeSeuil;
    const brut = ouvre ? Math.round((Math.min(revenu, plafond) / t.diviseur) * 100) / 100 : 0;
    const apresCarence = Math.max(0, jours - IJ_SOCIETE.carence);
    const joursIndemnises = reglemente ? Math.min(apresCarence, t.joursMaxLiberalReglemente) : apresCarence;
    return {
        forme: "eurl",
        ouvre,
        affilie,
        sousLeSeuil,
        reglemente,
        revenuMinimum: t.revenuMinimum,
        plafond,
        plafonne: revenu >= plafond,
        parJour: brut,
        maximum: plafond / t.diviseur,
        joursIndemnises,
        tronque: reglemente && apresCarence > t.joursMaxLiberalReglemente,
        total: brut * joursIndemnises,
        net: brut * joursIndemnises * (1 - IJ_SOCIETE.prelevements),
    };
}
